/**
 * Xattir wordmark — HTML text (not SVG `<text>`).
 * scroll-spine `svg { width:100%; height:auto }` plus the collapsed `.header_expand`
 * 4.125em slot crushed the SVG mark to a speck; a span sizes from `font-size`.
 */
export function BrandWordmark({ className }: { className?: string }) {
  return (
    <span
      className={["brand_wordmark", className].filter(Boolean).join(" ")}
      aria-hidden
    >
      Xattir
    </span>
  );
}

/**
 * Seek-overlay wordmark: outlined type with a fast bottom-up wavy fill.
 * Fill progress is driven by `--splash-fill` (0–1) from `transitionToSection`.
 */
export function BrandSplashMark({ className }: { className?: string }) {
  return (
    <span
      className={["splash_mark", "ltr_island", className]
        .filter(Boolean)
        .join(" ")}
      dir="ltr"
      aria-hidden
    >
      <span className="splash_mark_outline brand_wordmark">Xattir</span>
      <span className="splash_mark_fill_clip">
        <span className="splash_mark_wave" />
        <span className="splash_mark_fill brand_wordmark">Xattir</span>
      </span>
    </span>
  );
}

/**
 * Solid single-path X glyph (nav / pills / loader morph source).
 * Not the marketing multi-line mark — use `BrandMultiLineX` for lockups.
 */
export function BrandXMark({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      width="38"
      height="44"
      viewBox="0 0 38 44"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path
        d="M37.393 0L22.387 21.533L37.881 43.798H31.293L18.91 25.864L6.466 43.798H0L15.555 21.594L0.488 0H7.076L19.032 17.202L30.927 0H37.393Z"
        fill="currentColor"
      />
    </svg>
  );
}

/**
 * Official multi-line brand X (favicon geometry): triple-line strokes,
 * one diagonal continuous, the other interrupted at the cross.
 * Asset cropped from `public/favicon.svg` embedded PNG.
 */
export function BrandMultiLineX({ className }: { className?: string }) {
  return (
    // eslint-disable-next-line @next/next/no-img-element -- static brand mark; mask/currentColor not needed on white lockups
    <img
      className={className}
      src="/images/brand-x-mark.webp"
      alt=""
      width={512}
      height={478}
      decoding="async"
      aria-hidden
    />
  );
}

/**
 * Loader wordmark: official X SVG + "attir".
 * Hidden thin bars only exist as morph sources (never visible under the X).
 */
export function BrandLoaderMark() {
  return (
    <>
      <div className="loader_mark _ele">
        <BrandXMark className="loader_x_glyph" />
        <div className="loader_x_bars" aria-hidden>
          <i className="loader_line loader_line--a" />
          <i className="loader_line loader_line--b" />
        </div>
      </div>
      <span className="loader_suffix _ele">attir</span>
    </>
  );
}

export function ArrowIcon({ className }: { className?: string }) {
  return (
    <svg className={className} xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none" viewBox="0 0 18 18" aria-hidden>
      <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="m9.938 14.438 2.624-2.626-2.624-2.624" />
      <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M12.563 11.813h-4.5a3 3 0 0 1-3-3v-5.25" />
    </svg>
  );
}

export function JumpArrowIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" viewBox="0 0 16 16" aria-hidden>
      <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="m8.833 12.833 2.333-2.333-2.333-2.333" />
      <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M11.167 10.5h-4A2.667 2.667 0 0 1 4.5 7.833V3.167" />
    </svg>
  );
}

export function ListTickIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" viewBox="0 0 16 16" aria-hidden>
      <path stroke="currentColor" strokeLinecap="square" d="M6.667 10.667 4 8l2.667-2.667m4.666 5.334L8.667 8l2.666-2.667" />
    </svg>
  );
}
